import { LOCATION_CHANGE } from 'react-router-redux';

import {
  EXIT_SEARCH_REVIEWS,
  HIDE_SELECT_SIZE_TOOLTIP,
  HIGHLIGHT_SELECT_SIZE_TOOLTIP,
  PRODUCT_COLOR_CHANGED,
  PRODUCT_DESCRIPTION_TOGGLE,
  PRODUCT_SIZE_CHANGED,
  PRODUCT_SWATCH_CHANGE,
  RECEIVE_BRAND_PROMO,
  RECEIVE_CUSTOMER_AUTH_DETAILS,
  RECEIVE_GENERIC_SIZING_BIAS,
  RECEIVE_PDP_STORY_SYMPHONY_COMPONENTS,
  RECEIVE_PDP_SYMPHONY_COMPONENTS,
  RECEIVE_PRODUCT_DETAIL,
  RECEIVE_PRODUCT_REVIEWS,
  RECEIVE_SEARCH_REVIEWS,
  RECEIVE_SIMILAR_STYLES,
  RECEIVE_SIZING_PREDICTION_FAILURE,
  RECEIVE_SIZING_PREDICTION_SUCCESS,
  RECEIVE_UPVOTE_REVIEW_FAILURE,
  RECEIVE_UPVOTE_REVIEW_SUCCESS,
  REDIRECT,
  REQUEST_PDP_STORY_SYMPHONY_COMPONENTS,
  REQUEST_PDP_SYMPHONY_COMPONENTS,
  REQUEST_PRODUCT_DETAIL,
  REQUEST_PRODUCT_REVIEWS,
  REQUEST_SEARCH_REVIEWS,
  REQUEST_SIMILAR_STYLES,
  REQUEST_UPVOTE_REVIEW,
  SET_CAROUSEL_INDEX,
  SET_DOC_META_PDP,
  SET_DOC_META_PRODUCT_REVIEWS,
  SET_DOC_META_WRITE_REVIEW,
  SET_ERROR,
  SHOW_SELECT_SIZE_TOOLTIP,
  TOGGLE_OOS_BUTTON,
  TOGGLE_PDP_VISIBILITY,
  UNHIGHLIGHT_SELECT_SIZE_TOOLTIP,
  VALIDATE_DIMENSIONS
} from 'constants/reduxActions';
import {
  MOST_HELPFUL,
  PRODUCT_REVIEWS_PER_PAGE
} from 'constants/appConstants';
import { constructLayeredMsaImageSizingPositioning, constructLayeredMsaImageUrl, constructMSAImageUrl, filterThenMap } from 'helpers';
import ProductUtils from 'helpers/ProductUtils';
import { cleanDescription } from 'helpers/ProductDescriptionUtils';
import { buildDimensionValueLengthTypes } from 'helpers/productDimensionHelper';
import { buildSeoBrandString, buildSeoProductUrl } from 'helpers/SeoUrlBuilder';
import { createYouTubeContentUrl } from 'helpers/ClientUtils.js';
import cleanReviews from 'reducers/reviews/cleanReviews';

export const initialState = {
  selectedSizing: {},
  images: {},
  msaThumbnails: [],
  validation: {
    dimensions: {}
  },
  reviewData: {
    submittedReviews: [],
    loadingReviews: []
  },
  searchReviewData: {},
  isDescriptionExpanded: false,
  selectSizeTooltip: null,
  carouselIndex: 0,
  sizingPredictionId: null,
  isOnDemandEligible: null,
  brandPromo: {},
  authObj: {},
  availableDimensionsForColor: null,
  symphony: { loadingSymphonyComponents: false },
  symphonyStory: { loadingSymphonyStoryComponents: false, stories: [] },
  genericSizeBiases: null
};

// 1200px x 1200px
const layeredMsaDimensions = constructLayeredMsaImageSizingPositioning(1200);

// comparator for sorting styles by price
const priceSorter = ({ price: price1 }, { price: price2 }) => ProductUtils.priceToFloat(price1) - ProductUtils.priceToFloat(price2);
// comparator for sorting styles by color name alphabetically
const colorSorter = ({ color: color1 }, { color: color2 }) => (color1 < color2 ? -1 : (color1 > color2) ? 1 : 0);

function orderStyles(productType, styles) {
  if (Array.isArray(styles)) {
    const sortedStyles = styles.slice();
    const sorter = ProductUtils.isGiftCard(productType) ? priceSorter : colorSorter;
    sortedStyles.sort(sorter);
    return sortedStyles;
  }
  return styles;
}

function getSizingFromPrediction(sizingPredictionValue, currentSelectedSizing, colorId, { styles, sizing }) {
  const newSelectedSizing = { ...currentSelectedSizing };
  let sizingPredictionId = null;
  if (sizingPredictionValue) {
    for (const dim of sizing.dimensions) {
      const dimValues = dim.units[0].values;
      if (dim.name === 'size' && dimValues.length > 1) { // check if value in size array
        for (let i = 0; i < dimValues.length; i++) {
          // sizing prediction found in list of dimensions units
          if (dimValues[i].value === sizingPredictionValue) {
            sizingPredictionId = dimValues[i].id;
            newSelectedSizing[`d${dim.id}`] = sizingPredictionId;
            break;
          }
        }
        const style = ProductUtils.getStyleByColor(styles, colorId);
        if (!sizingPredictionId || !ProductUtils.getStockBySize(sizing.stockData, style.colorId, newSelectedSizing)) {
          // if product not found or out of stock, clear selectedSizing keys so prediction experience isn't shown
          sizingPredictionId = 'oos';
          delete newSelectedSizing[`d${dim.id}`];
        }
        break;
      }
    }
  }
  return Object.assign({
    sizingPredictionId,
    sizingPredictionValue,
    selectedSizing: newSelectedSizing
  });
}

// Converts style list from Product Bundle into array of thumbnails in the format of the legacy images
// returning an object in the form of { styleId, src, swatchSrc }
const makeMsaImageThumbnail = (({ images = [], styleId, swatchImageId, color, colorId, tsdImages }) => {
  // Use the first image provided by cloud cat as the thumbnail, per docs (https://confluence.zappos.net/display/~jsenecal/Cloud+Catalog+API)
  const thumbnailImageId = images.length ? images[0].imageId : '';
  const tsdThumbnailImageId = tsdImages?.imageIds?.[0] || '';
  return {
    color,
    colorId,
    src: constructMSAImageUrl(thumbnailImageId, { height: 82, width: 110, autoCrop: true }),
    tsdSrc: constructMSAImageUrl(tsdThumbnailImageId, { height: 152, width: 204, autoCrop: true, extension: 'png', customSettings: '_FMjpg' }),
    styleId,
    swatchSrc: constructMSAImageUrl(swatchImageId, { height: 19, width: 19 })
  };
});

const makeMsaImageThumbnails = styles => styles.map(style => makeMsaImageThumbnail(style));

// For the given color/style build a data structure that shows what size values for each dimension are in stock
const buildAvailableDimensionsForColor = (colorId, { dimensionsSet, stockData }) => {
  const available = { };

  dimensionsSet.forEach(dim => {
    available[dim] = {};
  });

  stockData.forEach(stock => {
    if (stock.color === colorId && +stock.onHand > 0) {
      dimensionsSet.forEach(dim => {
        if (!available[dim][stock[dim]]) {
          available[dim][stock[dim]] = true;
        }
      });
    }
  });

  return { available };
};

export function cleanReviewBadgeField(review, fieldName) {
  const fieldValue = review?.[fieldName];
  if (typeof fieldValue === 'string') {
    const lowerCaseFieldValue = fieldValue.toLowerCase();
    if (lowerCaseFieldValue === 'true') {
      review[fieldName] = true;
    } else if (lowerCaseFieldValue === 'false') {
      review[fieldName] = false;
    }
  }
}

export function cleanMostHelpfulReview(review) {
  [
    'customerRewardReview',
    'powerReview',
    'premierReview',
    'verifiedPurchase'
  ].forEach(fieldName => cleanReviewBadgeField(review, fieldName));
}

export default function productDetailReducer(state = initialState, action) {
  const {
    type,
    product,
    reviewData,
    similarStylesData,
    dimensions,
    reviewsOffset,
    reviewsPage,
    productId,
    index,
    orderBy,
    searchTerm,
    searchResults,
    reviewId,
    sizingPredictionValue,
    colorId,
    isOnDemandEligible,
    brandPromo,
    lookupKeyObject = {},
    authObj,
    genericSizeBiases,
    symphony,
    symphonyStory,
    calledClientSide,
    oosModalActive
  } = action;
  switch (type) {
    case REQUEST_PRODUCT_DETAIL:
      return Object.assign({}, state, { isLoading: true, selectedSizing: {}, sizingPredictionId: null, sizingPredictionValue: null });
    case SET_DOC_META_PDP:
    case SET_DOC_META_WRITE_REVIEW:
    case SET_DOC_META_PRODUCT_REVIEWS:
      // this is a separate action from RECEIVE_PRODUCT_DETAIL since non-product pages also use these actions (mistakes were made)
      return { ...state, isLoading: false };
    case RECEIVE_PRODUCT_DETAIL:
      const stylesWithCleanedImages = [];
      const {
        detail : {
          reviewSummary: { reviewWithLeastVotes, reviewWithMostVotes } = {},
          productName,
          videos,
          youtubeVideoId
        }
      } = product;
      product.detail.styles.forEach(style => {
        const cleanedUpStyle = { ...style, tsdImages: { ...style.tsdImages } };
        // remove MAIN image if PAIR image exists for MSA image bundle from image list but throw it in a separate field for use
        if (style.images) {
          const mainImage = style.images.find(image => image.type === 'MAIN');
          if (mainImage) {
            cleanedUpStyle.imageId = mainImage.imageId;
            if (style.images.some(image => image.type === 'PAIR')) {
              cleanedUpStyle.images = style.images.filter(image => image.type !== 'MAIN');
            }
          }
        }

        const tsdImageIds = style.tsdImages?.imageIds;
        const tsdBackgroundImageId = style.tsdImages?.backgroundId;
        if (tsdImageIds && tsdBackgroundImageId) {
          // Basically we layer the shoe image on top of the circle image. And create a single new jpg image. This is MUCH
          // lighter than a png. ~800kb vs 75kb.
          cleanedUpStyle.tsdImages.imageUrls = tsdImageIds.map(shoeImgId => constructLayeredMsaImageUrl({
            ...layeredMsaDimensions,
            topImageId: shoeImgId,
            botImageId: tsdBackgroundImageId
          }));
        }

        // Only add to the state if we have an actual image to show
        if (cleanedUpStyle.images?.length || cleanedUpStyle.tsdImages?.imageIds?.length) {
          stylesWithCleanedImages.push(cleanedUpStyle);
        }
      });

      cleanMostHelpfulReview(reviewWithLeastVotes);
      cleanMostHelpfulReview(reviewWithMostVotes);

      const { brandId, brandName, description: receivedDescription, overallRating: receivedOverallRating = [], sizing : { allValues : allSizes } } = product.detail;
      const overallRating = [];
      for (let i = 1; i <= 5; i++) {
        overallRating.push(receivedOverallRating[6 - i] || '0');
      }

      const youtubeData = youtubeVideoId
        ? {
          videoId : youtubeVideoId,
          contentUrl : createYouTubeContentUrl(youtubeVideoId),
          embedUrl : `https://www.youtube.com/embed/${youtubeVideoId}`,
          thumbnailUrl : `https://img.youtube.com/vi/${youtubeVideoId}/hqdefault.jpg`,
          // Unfortunately we are not able to get the actual metadata from the youtube video so these will have to do
          videoName : productName,
          uploadDate : videos.length > 0 && videos[0].uploadedDate
        }
        : {}
      ;

      const hypercubeSizingData = allSizes && allSizes.reduce((result, size, index) => ({
        ...result,
        [size.id] : {
          min : parseInt(size.rank, 10),
          max : allSizes[index + 1] ? parseInt(allSizes[index + 1].rank, 10) - 1 : Number.MAX_SAFE_INTEGER
        }
      }), {});

      const description
        = receivedDescription
          ? cleanDescription(receivedDescription, { [brandName]: buildSeoBrandString(brandName, brandId) })
          : receivedDescription;
      const styles = orderStyles(product.detail.defaultProductType, stylesWithCleanedImages);
      const sizing = {
        ...product.detail.sizing,
        hypercubeSizingData
      };

      let detail = Object.assign({}, product.detail, { receivedDescription, description, styles, overallRating, youtubeData, sizing });

      if (detail?.reviewSummary?.overallRating) {
        const { aggregateRating } = [5, 4, 3, 2, 1].reduce((acc, num) => {
          const numTotal = parseInt(detail.reviewSummary.overallRating[num], 10);
          if (!Number.isNaN(numTotal)) {
            acc.overallTotal += numTotal;
            acc.reviewWeight += numTotal * num;
          }
          return acc;
        }, {
          overallTotal: 0,
          reviewWeight: 0,
          get aggregateRating() {
            return this.reviewWeight / this.overallTotal;
          }
        });

        detail = { ...detail, reviewSummary: { ...detail.reviewSummary, aggregateRating } };
      }
      const seoProductUrl = buildSeoProductUrl(product.detail); // explicitly omit color since this should be the generic canonical.
      const reviewCount = product.detail.reviewCount || 0;
      const reviewsTotalPages = reviewCount > 0 ? Math.ceil(reviewCount / PRODUCT_REVIEWS_PER_PAGE) : 0;
      const styleThumbnails = makeMsaImageThumbnails(detail.styles);
      const availableDimensionsForColor = buildAvailableDimensionsForColor(colorId || detail.styles[0]?.colorId, detail.sizing);
      const dimensionValueLengthTypes = buildDimensionValueLengthTypes(detail.sizing.dimensions);
      const newState = Object.assign({}, state, product, {
        detail,
        selectedSizing: {},
        styleThumbnails,
        validation: { dimensions: {} },
        isDescriptionExpanded: false,
        reviewsTotalPages,
        colorId,
        requestedAsin: lookupKeyObject.asin,
        requestedStockId: lookupKeyObject.stockId,
        seoProductUrl,
        availableDimensionsForColor,
        dimensionValueLengthTypes,
        calledClientSide
      });
      for (const dim of product.detail.sizing.dimensions) {
        if (dim.units[0].values.length === 1) {
          newState.selectedSizing[`d${dim.id}`] = dim.units[0].values[0].id;
        }
      }
      if (state.sizingPredictionId && state.sizingPredictionId !== 'oos' && state.sizingPredictionValue) {
        const sizingPredictions = getSizingFromPrediction(state.sizingPredictionValue, newState.selectedSizing, colorId, newState.detail);
        return { ...newState, ...sizingPredictions };
      }
      return newState;
    case PRODUCT_SWATCH_CHANGE:
      return { ...state, colorId };
    case PRODUCT_SIZE_CHANGED:
      const cleanedSizing = {};
      for (const dimensionId of Object.keys(dimensions)) {
        if (dimensions[dimensionId]) {
          cleanedSizing[dimensionId] = dimensions[dimensionId];
        }
      }
      return Object.assign({}, state, {
        selectedSizing: cleanedSizing
      });
    case REQUEST_PRODUCT_REVIEWS:
      return { ...state, reviewData: { ...state.reviewData, isLoading: true, page: reviewsPage, offset: reviewsOffset, orderBy } };
    case RECEIVE_PRODUCT_REVIEWS:
      return {
        ...state,
        reviewData: {
          ...state.reviewData,
          isLoading: false,
          reviews: cleanReviews(reviewData.reviews),
          productId
        }
      };
    case REQUEST_SEARCH_REVIEWS:
      return Object.assign({},
        state,
        {
          isLoading: true,
          isSearchingReviews: true,
          searchReviewData: {
            ...state.searchReviewData,
            searchTerm,
            page: reviewsPage
          }
        });
    case RECEIVE_SEARCH_REVIEWS:
      return Object.assign({}, state, {
        isLoading: false,
        reviewData: {
          ...state.reviewData,
          orderBy: MOST_HELPFUL // search is always most helpful first
        },
        searchReviewData: {
          ...state.searchReviewData,
          reviews: cleanReviews(searchResults.reviews),
          totalPages: Math.floor(searchResults.totalHits / 25),
          totalReviews: searchResults.totalHits
        }
      });
    case EXIT_SEARCH_REVIEWS:
      return Object.assign({}, state, { isSearchingReviews: false, searchReviewData: { reviews: [], searchTerm: '' } });
    case REQUEST_SIMILAR_STYLES:
      return Object.assign({}, state, {
        isSimilarStylesLoading: true
      });
    case RECEIVE_SIMILAR_STYLES:
      const filteredStyles = filterThenMap(similarStylesData.results,
        (style => `${style.productId}` !== productId),
        (style => ({ ...style, productSeoUrl: style.productUrl }))); // search similarity response doesn't populate this, but MelodyCardProduct needs it
      const data = { ...similarStylesData, results: filteredStyles };
      return Object.assign({}, state, {
        isSimilarStylesLoading: false,
        similarStyles: data
      });
    case PRODUCT_DESCRIPTION_TOGGLE:
      return Object.assign({}, state, {
        isDescriptionExpanded: !state.isDescriptionExpanded
      });
    case TOGGLE_OOS_BUTTON:
      return Object.assign({}, ...state, {
        oosButtonClicked: oosModalActive
      });
    case VALIDATE_DIMENSIONS:
      const addToCartBeenClicked = action.showValidation ? true : state.validation.showValidation;
      const validatedState = Object.assign({}, state, {
        validation: {
          showValidation: addToCartBeenClicked,
          dimensions: {}
        }
      });
      state.detail.sizing.dimensionsSet.forEach(dimId => {
        // a dimension is invalid if it is true
        validatedState.validation.dimensions[dimId] = addToCartBeenClicked && !state.selectedSizing[dimId];
      });
      return validatedState;
    case SHOW_SELECT_SIZE_TOOLTIP:
      return { ...state, isSelectSizeTooltipVisible: true };
    case HIDE_SELECT_SIZE_TOOLTIP:
    case LOCATION_CHANGE:
      return { ...state, isSelectSizeTooltipVisible: false, isSelectSizeTooltipHighlighted: false };
    case HIGHLIGHT_SELECT_SIZE_TOOLTIP:
      return { ...state, isSelectSizeTooltipHighlighted: true };
    case UNHIGHLIGHT_SELECT_SIZE_TOOLTIP:
      return { ...state, isSelectSizeTooltipHighlighted: false };
    case SET_CAROUSEL_INDEX:
      return Object.assign({}, state, {
        carouselIndex: index
      });
    case REQUEST_UPVOTE_REVIEW:
      return {
        ...state,
        reviewData: {
          ...state.reviewData,
          loadingReviews: state.reviewData.loadingReviews.concat(reviewId)
        }
      };
    case RECEIVE_UPVOTE_REVIEW_SUCCESS:
      return {
        ...state,
        reviewData: {
          ...state.reviewData,
          loadingReviews: state.reviewData.loadingReviews.filter(id => id !== reviewId),
          submittedReviews: state.reviewData.submittedReviews.concat(reviewId)
        }
      };
    case RECEIVE_UPVOTE_REVIEW_FAILURE:
      return {
        ...state,
        reviewData: {
          ...state.reviewData,
          loadingReviews: state.reviewData.loadingReviews.filter(id => id !== reviewId)
        }
      };
    case REDIRECT:
    case SET_ERROR:
      return Object.assign({}, state, { isLoading: false });
    case RECEIVE_SIZING_PREDICTION_SUCCESS:
      // This _should_ always be loaded, but if it isn't we shouldn't blow up
      if (state.selectedSizing && state.detail) {
        const predictedSizing = getSizingFromPrediction(sizingPredictionValue, state.selectedSizing, colorId, state.detail);
        return { ...state, ...predictedSizing };
      }
      return state;

    case RECEIVE_SIZING_PREDICTION_FAILURE:
      return { ...state, isOnDemandEligible };
    case RECEIVE_GENERIC_SIZING_BIAS:
      return { ...state, genericSizeBiases };
    case RECEIVE_BRAND_PROMO:
      return Object.assign({}, state, {
        brandPromo
      });
    case RECEIVE_CUSTOMER_AUTH_DETAILS:
      return Object.assign({}, state, {
        authObj
      });
    case PRODUCT_COLOR_CHANGED:
      return { ...state, availableDimensionsForColor: buildAvailableDimensionsForColor(colorId, state.detail.sizing) };
    case REQUEST_PDP_SYMPHONY_COMPONENTS:
      return { ...state, symphony: { ...state.symphony, loadingSymphonyComponents: true } };
    case RECEIVE_PDP_SYMPHONY_COMPONENTS:
      const contentStyleId = Object.keys(symphony?.style || {}).length ? symphony.style : null;
      const contentProductId = Object.keys(symphony?.product || {}).length ? symphony.product : null;
      const pageId = contentStyleId ? `s${symphony.styleId}` : contentProductId ? `p${symphony.productId}` : null;
      return {
        ...state, symphony: {
          ...state.symphony,
          ...symphony,
          [`s${symphony.styleId}`]: contentStyleId,
          [`p${symphony.productId}`]: contentProductId,
          loadingSymphonyComponents: false,
          pageId
        }
      };
    case REQUEST_PDP_STORY_SYMPHONY_COMPONENTS:
      return {
        ...state,
        symphonyStory: { ...state.symphonyStory, loadingSymphonyStoryComponents: true }
      };
    case RECEIVE_PDP_STORY_SYMPHONY_COMPONENTS:
      const stories = symphonyStory.slotNames.reduce((acc, slotName) => {
        if (slotName.includes('story')) {
          acc.push({ ...symphonyStory.slotData[slotName], slotName });
        }
        return acc;
      }, []);
      return {
        ...state,
        symphonyStory: { ...symphonyStory, stories, loadingSymphonyStoryComponents: false }
      };
    default:
      return state;
  }
}

export function isPdpVisible(state = false, action) {
  const { type, visible } = action;
  switch (type) {
    case TOGGLE_PDP_VISIBILITY:
      return visible;
    default:
      return state;
  }
}
